import React, { ButtonHTMLAttributes, forwardRef } from 'react'

import { BoxProps } from '../Box/Box'
import { Icon, IconName, IconProps } from '../Icon'
import { MotionStack } from '../Motion/MotionComponents'

import { buttonStyle, ButtonStyleVariants } from './Button.css'

type StyleProps = Omit<NonNullable<ButtonStyleVariants>, 'active'>

type Props = {
    children?: React.ReactNode
    tone?: BoxProps['background']
    icon?: IconName
    disabled?: boolean
    minWidth?: BoxProps['minWidth']
    width?: BoxProps['width']
    animate?: boolean
    // compact?: boolean
    type?: 'button' | 'submit' | 'reset'
    onClick?: (e: React.MouseEvent) => void
} & Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    'onDrag' | 'onDragEnd' | 'onDragStart' | 'onAnimationStart' | 'size' | 'color'
> &
    StyleProps &
    Pick<
        BoxProps,
        | 'flexGrow'
        | 'flexBasis'
        | 'grow'
        | 'aspectRatio'
        | 'color'
        | 'fontWeight'
        | 'inset'
        | 'insetX'
        | 'insetY'
        | 'border'
        | 'justifyContent'
        | 'gap'
    >

export type ButtonProps = Props

type ButtonSize = NonNullable<StyleProps['size']>

const iconSize: { [key in ButtonSize]: IconProps['size'] } = {
    inline: 'square_inline',
    button_xs: 'square_xxs',
    button_sm: 'square_xs',
    button_x5: 'square_x5',
    button_md: 'square_sm',
    button_lg: 'square_md',
    button_rounded_md: 'square_sm',
} as const

export const Button = forwardRef<HTMLButtonElement, Props>(
    (
        {
            aspectRatio,
            animate = false,
            size = 'button_md',
            rounded = 'full',
            disabled,
            hoverEffect,
            tone = 'level3',
            type = 'button',
            icon,
            children,
            onClick,
            ...inputProps
        },
        ref,
    ) => (
        <MotionStack
            horizontal
            hoverable
            layout={animate}
            aspectRatio={aspectRatio}
            as="button"
            type={type}
            cursor={disabled ? 'default' : 'pointer'}
            className={buttonStyle({
                size,
                rounded,
                hoverEffect,
                border: inputProps.border,
                // a tone can be specified here in order to transition background
                // tone: animate || tone == 'level3' ? tone : undefined,
            })}
            justifyContent="center"
            alignItems="center"
            background={tone}
            whileHover="hover"
            disabled={disabled}
            onClick={onClick}
            {...inputProps}
            ref={ref}
        >
            {icon && (
                <Icon type={icon} size={size in iconSize ? iconSize[size] : 'square_inline'} />
            )}
            {children}
        </MotionStack>
    ),
)
